<template>
  <div class="headline flex flex-col-reverse md:flex-row justify-between items-start md:items-center">
    <h2 class="text-xl sm:text-2xl font-semibold pb-5 pt-5 text-blue-600 flex gap-x-3 items-end">
      <div class="flex flex-col -mt-5">
        <slot name="page-title">
          <span class="page-type text-sm sm:text-base">Package</span>
          {{ title }}
        </slot>
      </div>
    </h2>

    <div class="relative inline-flex shadow-sm rounded-md md:items-start items-end self-end md:self-auto mt-2">
      <button
        v-if="editing"
        @click="$emit('edit')"
        class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Cancel
      </button>

      <button
        v-if="editing"
        @click="saveClicked"
        type="button"
        class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Save Changes
      </button>

      <Menu
        as="div"
        class="relative inline-block text-left"
      >
        <div>
          <MenuButton
            class="inline-flex justify-center w-full rounded-md border border-gray-400 shadow-sm px-4 py-3 bg-gray-100 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-indigo-500"
          >
            Actions
            <ChevronDownIcon
              class="-mr-1 ml-2 h-5 w-5"
              aria-hidden="true"
            />
          </MenuButton>
        </div>

        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <MenuItems
            class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50"
          >
            <div class="py-1">
              <MenuItem v-slot="{ active }">
                <a
                  href="#"
                  @click="editClicked"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <PencilIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Edit
                </a>
              </MenuItem>
            </div>
            <div class="py-1">
              <MenuItem v-slot="{ active }">
                <a
                  href="#"
                  @click="$emit('assign')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <PencilAltIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Assign to Product
                </a>
              </MenuItem>
              <MenuItem
                v-slot="{ active }"
                v-if="hasPermission('adjust_packages')"
              >
                <a
                  href="#"
                  @click="$emit('adjust')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <SwitchVerticalIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Adjust Package
                </a>
              </MenuItem>
              <MenuItem
                v-slot="{ active }"
                v-if="!hasPermission('adjust_packages')"
              >
                <span class="text-gray-400 group flex items-center px-4 py-2 text-sm">
                  <SwitchVerticalIcon
                    class="mr-3 h-5 w-5 text-gray-300"
                    aria-hidden="true"
                  />
                  Adjust Package
                </span>
              </MenuItem>
              <MenuItem
                v-slot="{ active }"
                v-if="hasPermission('balance_packages') || isAdmin"
              >
                <a
                  href="#"
                  @click="$emit('balance')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <SwitchVerticalIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Balance Package
                </a>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <a
                  href="#"
                  @click="$emit('showlabels')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <QrcodeIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Show Label
                </a>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <a
                  href="#"
                  @click="$emit('label')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <QrcodeIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Print Labels
                </a>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <a
                  href="#"
                  @click="$emit('transfer')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <DuplicateIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Transfer Package
                </a>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <a
                  href="#"
                  @click="$emit('subLot')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <DuplicateIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Create SubLot
                </a>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <a
                  @click.prevent="$emit('audit')"
                  href="#"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <SearchIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Audit
                </a>
              </MenuItem>
            </div>
            <div class="py-1">
              <MenuItem v-slot="{ active }">
                <a
                  href="#"
                  v-if="!isArchived"
                  @click="$emit('archive')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <ArchiveIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Archive
                </a>
                <a
                  href="#"
                  v-else
                  @click="$emit('restore')"
                  :class="[
                    'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                  ]"
                >
                  <ArchiveIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                    aria-hidden="true"
                  />
                  Restore
                </a>
              </MenuItem>
            </div>
            <div
              class="py-1"
              v-if="isAdmin && novaRoute"
            >
              <MenuItem v-slot="{ active }">
                <NovaMenuButton :nova-route="novaRoute" />
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </div>
  </div>
</template>

<script>
import {
  ArchiveIcon,
  ChevronDownIcon,
  PencilIcon,
  DuplicateIcon,
  PencilAltIcon,
  BookOpenIcon,
  SwitchVerticalIcon,
  SearchIcon,
  QrcodeIcon
} from '@heroicons/vue/solid'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import PublishSelector from '@/Components/Menus/PublishSelector'
import hasAuth from '@/Mixins/hasAuth'
import NovaMenuButton from '@/Components/Buttons/NovaMenuButton.vue'

export default {
  mixins: [hasAuth],

  components: {
    PencilAltIcon,
    Menu,
    MenuButton,
    PencilIcon,
    MenuItem,
    MenuItems,
    ArchiveIcon,
    DuplicateIcon,
    ChevronDownIcon,
    BookOpenIcon,
    SwitchVerticalIcon,
    SearchIcon,
    QrcodeIcon,
    PublishSelector,
    NovaMenuButton
  },

  props: {
    packageId: {
      type: Number,
      required: true
    },

    title: {
      type: String,
      default: ''
    },

    subtitle: {
      type: String,
      defualt: ''
    },

    /**
     * If true the save button will be shown
     */
    editing: {
      type: Boolean,
      default: false
    },

    isArchived: {
      type: Boolean,
      default: false
    },
    novaRoute: {
      type: String,
      default: null
    }
  },

  methods: {
    editClicked() {
      this.$emit('edit')
    },

    saveClicked() {
      this.$emit('save')
    }
  }
}
</script>
